<template>
  <div class="template-usage w-100">
    <v-sheet
      v-if="listLoading"
      class="d-flex flex-column justify-center"
      min-height="200px"
    >
      <div class="mx-auto">
        <v-progress-circular
          :size="40"
          color="primary"
          indeterminate
        />
      </div>
    </v-sheet>

    <TemplateUsageConfirm
      v-else-if="view === 'confirm'"
      :selected-count="selectedUlids.length"
      @back="handleBackToList"
      @confirm="handleConfirmUpdate"
    />

    <TemplateUsageList
      v-else
      :rows="sortedRows"
      :selected-ulids="selectedUlids"
      :outdated-count="outdatedCount"
      :profile-count="templateProfiles.length"
      @toggle="handleToggleProfile"
      @select-outdated="handleSelectOutdated"
      @open-confirm="handleOpenConfirm"
    />
  </div>
</template>

<script setup>
import {
  computed,
  onMounted,
  ref,
  watch,
} from 'vue';
import { storeToRefs } from 'pinia';
import { useCurrentJobProfile } from '@/stores/jobProfiles/currentJobProfile';
import { useAppLoading } from '@/stores/app/loading';
import { useAppSnackbar } from '@/stores/app/snackbar';
import TemplateUsageConfirm from '@/components/jobProfiles/editor/TemplateUsageConfirm.vue';
import TemplateUsageList from '@/components/jobProfiles/editor/TemplateUsageList.vue';

const STATUS = {
  OUTDATED: 'outdated',
  UPDATING: 'updating',
  OK: 'ok',
};

const STATUS_SORT_ORDER = {
  [STATUS.UPDATING]: 0,
  [STATUS.OUTDATED]: 1,
  [STATUS.OK]: 2,
};

const getProfileStatus = ({ profileHash, templateHash, isUpdating }) => {
  if (isUpdating) {
    return STATUS.UPDATING;
  }

  if (profileHash && templateHash && profileHash !== templateHash) {
    return STATUS.OUTDATED;
  }

  return STATUS.OK;
};

const currentJobProfile = useCurrentJobProfile();
const {
  templateProfiles,
  jobProfile,
  finalizedLockedFieldsHash,
} = storeToRefs(currentJobProfile);
const { listLoading } = storeToRefs(useAppLoading());
const { addErrorSnackbar } = useAppSnackbar();

const view = ref('list');
const selectedUlids = ref([]);
const updatingUlids = ref([]);

// Match apply-template: compare against the finalized template hash
const templateHash = computed(() => finalizedLockedFieldsHash.value);

const rows = computed(() => templateProfiles.value.map((profile) => ({
  profile,
  status: getProfileStatus({
    profileHash: profile.locked_fields_hash,
    templateHash: templateHash.value,
    isUpdating: updatingUlids.value.includes(profile.ulid),
  }),
})));

// Sort rows by status, then title
const sortedRows = computed(() => [...rows.value].sort((a, b) => {
  const byStatus = STATUS_SORT_ORDER[a.status] - STATUS_SORT_ORDER[b.status];

  if (byStatus !== 0) {
    return byStatus;
  }

  const titleA = a.profile.title || '';
  const titleB = b.profile.title || '';

  return titleA.localeCompare(titleB);
}));

const outdatedRows = computed(() => (
  rows.value.filter(({ status }) => status === STATUS.OUTDATED)
));

const outdatedCount = computed(() => outdatedRows.value.length);

const handleToggleProfile = (ulid) => {
  if (selectedUlids.value.includes(ulid)) {
    selectedUlids.value = selectedUlids.value.filter((id) => id !== ulid);
    return;
  }

  selectedUlids.value = [...selectedUlids.value, ulid];
};

const handleSelectOutdated = () => {
  const outdatedUlids = outdatedRows.value.map(({ profile }) => profile.ulid);
  const allOutdatedSelected = outdatedUlids.length > 0
    && outdatedUlids.every((ulid) => selectedUlids.value.includes(ulid));

  selectedUlids.value = allOutdatedSelected ? [] : outdatedUlids;
};

const handleOpenConfirm = () => {
  if (!selectedUlids.value.length) return;

  view.value = 'confirm';
};

const handleBackToList = () => {
  view.value = 'list';
};

const markProfileUpToDate = (ulid) => {
  if (!templateHash.value) return;

  templateProfiles.value = templateProfiles.value.map((profile) => (
    profile.ulid === ulid
      ? { ...profile, locked_fields_hash: templateHash.value }
      : profile
  ));
};

const updateOneProfile = async (ulid) => {
  try {
    await currentJobProfile.applyTemplateToProfile({ profileUlid: ulid });

    markProfileUpToDate(ulid);
  } catch (err) {
    addErrorSnackbar({
      text: `Failed to update profile — ${err.message || err}`,
    });
  } finally {
    updatingUlids.value = updatingUlids.value.filter((id) => id !== ulid);
  }
};

const handleConfirmUpdate = async () => {
  updatingUlids.value = selectedUlids.value;
  selectedUlids.value = [];
  view.value = 'list';

  const ulidsToUpdate = updatingUlids.value;

  for (let i = 0; i < ulidsToUpdate.length; i += 1) {
    // eslint-disable-next-line no-await-in-loop
    await updateOneProfile(ulidsToUpdate[i]);
  }
};

onMounted(() => {
  currentJobProfile.loadProfilesByTemplate();
});

// Only reload when switching templates — not on every rescore of the open draft
watch(() => jobProfile.value?.ulid, (ulid) => {
  if (!ulid) return;

  selectedUlids.value = [];
  updatingUlids.value = [];
  view.value = 'list';
  currentJobProfile.loadProfilesByTemplate();
});

// Keep selectedUlids updated when outdatedRows changes
watch(outdatedRows, (nextOutdated) => {
  const outdatedUlids = new Set(nextOutdated.map(({ profile }) => profile.ulid));

  selectedUlids.value = selectedUlids.value.filter((ulid) => outdatedUlids.has(ulid));
});
</script>
